/* ========== STANDARD CHART PARSER ==========
 * Parses an OOXML <c:chart> document into a renderer-friendly object.
 *
 * Flow:
 *   1. Read the title (rich/strCache/t fallbacks)
 *   2. Detect every chart-type element in plotArea (chartTypeDetector)
 *   3. Pick a primary chart type — special-cases barDir → col, ofPieType
 *      → pieOfPie/barOfPie
 *   4. Parse every <c:ser> across all groups (seriesParser)
 *   5. Read shared categories (seriesParser)
 *   6. Pull ofPie geometry through from the primary group
 *
 * Extended charts (Treemap, Sunburst, etc.) go through extendedChartParser
 * instead, since they use a completely different XML schema.
 * ============================================ */

import { qLocal, qLocalAll, qDirectChild } from "../xmlUtils";
import { detectChartGroups } from "./chartTypeDetector";
import { parseSeries, readCategories, readCategoryLevels } from "./seriesParser";
import { getThemeSeriesPalette } from "./colorResolver";
import { COLORS } from "../../charts/utils";

// Chart types coloured ONE colour PER SERIES (each series = one accent).
const SERIES_COLORED_TYPES = {
  line: true, col: true, bar: true, area: true,
  radar: true, scatter: true, bubble: true, stock: true, surface: true,
};
// Chart types coloured PER DATA POINT (each slice/segment = one accent).
const POINT_COLORED_TYPES = {
  pie: true, doughnut: true, pieOfPie: true, barOfPie: true,
};

/**
 * Fill in automatic chart colours from the workbook theme when the file
 * didn't author explicit colours.  Excel cycles accent1..accent6 — by
 * SERIES for line/column/bar/area, by POINT for pie/doughnut.
 *
 * Zero-regression guarantee: beyond the 6 theme accents we fall back to
 * the renderer's existing COLORS palette, AND for the default Office theme
 * the accents are exactly COLORS[0..5] — so colorAt(i) === COLORS[i] for
 * every index on a default-theme workbook.  Only NON-default themes (the
 * actual "different theme" defect) change anything.
 */
function applyThemeSeriesColors(result) {
  const accents = getThemeSeriesPalette();
  if (!accents) return; // no theme loaded → renderers already use COLORS
  const colorAt = (i) => accents[i] || COLORS[i % COLORS.length];

  if (SERIES_COLORED_TYPES[result.chartType] || result.isCombo) {
    result.series.forEach((s, i) => {
      if (!s.color) s.color = colorAt(i);
    });
  } else if (POINT_COLORED_TYPES[result.chartType]) {
    result.series.forEach((s) => {
      const n = (s.data && s.data.length) || 0;
      if (n === 0) return;
      const pc = s.pointColors ? s.pointColors.slice() : [];
      for (let i = 0; i < n; i += 1) {
        if (!pc[i]) pc[i] = colorAt(i);
      }
      s.pointColors = pc;
    });
  }
}

/**
 * Extract the chart title. Excel writes this in several different ways
 * depending on version; we try them in order until one returns text.
 */
function readChartTitle(chartDoc) {
  const chartElem = qLocal(chartDoc, "chart");
  const titleNode = chartElem ? qDirectChild(chartElem, "title") : qLocal(chartDoc, "title");
  if (!titleNode) return "Chart";

  // 1. rich text inside tx
  const txNode = qLocal(titleNode, "tx");
  if (txNode) {
    const richNode = qLocal(txNode, "rich");
    if (richNode) {
      const tNodes = qLocalAll(richNode, "t");
      if (tNodes.length > 0) {
        const text = tNodes.map(t => t.textContent).join("").trim();
        if (text) return text;
      }
    }
    // 2. cached string ref
    const strCache = qLocal(txNode, "strCache");
    if (strCache) {
      const vNode = qLocal(strCache, "v");
      if (vNode && vNode.textContent.trim()) return vNode.textContent.trim();
    }
  }
  // 3. fallback to any <a:t> under the title
  const fallbackTexts = qLocalAll(titleNode, "t");
  if (fallbackTexts.length > 0) {
    const text = fallbackTexts.map(t => t.textContent).join("").trim();
    if (text) return text;
  }
  return "Chart";
}

/**
 * Decide the final chartType string the renderer's switch will see.
 *   - barChart + barDir="bar" → "bar" (horizontal)
 *   - barChart + barDir="col" → "col" (vertical)
 *   - ofPieChart + ofPieType="bar" → "barOfPie"
 *   - ofPieChart + ofPieType="pie" → "pieOfPie"
 *   - everything else passes its renderType through unchanged
 */
function resolvePrimaryChartType(primary) {
  if (primary.renderType === "bar") {
    return primary.barDir === "col" ? "col" : "bar";
  }
  if (primary.renderType === "ofPie") {
    return primary.ofPieType === "bar" ? "barOfPie" : "pieOfPie";
  }
  return primary.renderType;
}

export function parseStandardChart(chartDoc) {
  const result = {
    title:      readChartTitle(chartDoc),
    chartType:  "bar",
    isCombo:    false,
    series:     [],
    categories: [],
  };

  const plotArea = qLocal(chartDoc, "plotArea");
  if (!plotArea) return result;

  const chartGroups = detectChartGroups(plotArea);
  if (chartGroups.length === 0) return result;

  // Primary group decides chartType / grouping / 3D / markers.
  const primary = chartGroups[0];
  result.chartType = resolvePrimaryChartType(primary);
  result.grouping  = primary.grouping;
  result.is3D      = primary.is3D;
  result.hasMarker = primary.hasMarker;
  if (primary.ofPieGeom) result.ofPieGeom = primary.ofPieGeom;
  if (primary.radarStyle) result.radarStyle = primary.radarStyle;
  if (primary.dataLabels) result.dataLabels = primary.dataLabels;

  // Combo = chart has multiple distinct render types in its plotArea.
  const uniqueTypes = Array.from(new Set(chartGroups.map(g => g.renderType)));
  result.isCombo = uniqueTypes.length > 1;

  // Walk every series across every chart group. Excel may write <c:ser>
  // elements out of display order — <c:order val="N"/> is authoritative.
  // Without sorting, stacked charts stack segments in the wrong order and
  // clustered charts show series swapped ("wrong data" symptoms).
  const allSerNodes = [];
  const collected = [];
  for (const group of chartGroups) {
    const serNodes = qLocalAll(group.element, "ser");
    serNodes.forEach(ser => {
      const seriesObj = parseSeries(ser, group.renderType, group.barDir);
      if (seriesObj) {
        const orderNode = qLocal(ser, "order");
        const orderVal = orderNode ? parseInt(orderNode.getAttribute("val") || "0", 10) : collected.length;
        collected.push({ order: isNaN(orderVal) ? collected.length : orderVal, seriesObj });
      }
      allSerNodes.push(ser);
    });
  }
  collected.sort((a, b) => a.order - b.order);
  collected.forEach(c => result.series.push(c.seriesObj));

  result.categories = readCategories(allSerNodes);
  result.catLevels = readCategoryLevels(allSerNodes);

  // Automatic series colours from the workbook theme (no-op for files that
  // author explicit colours or use the default Office theme).
  applyThemeSeriesColors(result);
  return result;
}
